import {Component, Inject, OnDestroy, OnInit, signal} from '@angular/core';
import {Folder, Mutation} from '../../../types-repository';
import {Clipboard} from '@angular/cdk/clipboard';
import {BannerService} from '../../../banner/banner.service';
import {Apollo} from 'apollo-angular';
import {CREATE_UPLOAD_TICKET} from '../../../queries-repository';
import {DIALOG_DATA, DialogRef} from '@angular/cdk/dialog';
import {environment} from '../../../../environments/environment';
import {DomSanitizer, SafeResourceUrl} from '@angular/platform-browser';
import {DoButtonComponent} from '../../do-button/do-button.component';
import {ClTabsModule} from '../../cl-tabs/cl-tabs.module';
import {FormsModule} from '@angular/forms';
import {FormFooterComponent} from '../../form-footer/form-footer.component';
import {DateComponent} from '../../../date/date.component';

/**
 * The uploader iframe URL: embedded, in the portal's current light/dark mode (castlabs-ui's
 * #cl-mode-dark switch), so the uploader matches the page it sits in (VTK-3578).
 */
export function uploaderEmbedUrl(url: string, doc: Document = document): string {
  const dark = !!doc.querySelector<HTMLInputElement>('#cl-mode-dark')?.checked;
  return `${url}?embed&theme=${dark ? 'dark' : 'light'}`;
}

@Component({
  selector: 'app-generate-upload-link-dialog',
  templateUrl: './generate-upload-link-dialog.component.html',
  styleUrls: ['./generate-upload-link-dialog.component.scss'],
  imports: [
    FormFooterComponent,
    ClTabsModule,
    FormsModule,
    DateComponent
]
})
export class GenerateUploadLinkComponent implements OnInit, OnDestroy {
  message: string;
  feedback: string;
  folder: Folder;

  /** Which copy button last copied successfully — drives its "copied" checkmark. */
  copied = signal<'link' | 'aws' | null>(null);
  private copiedResetTimeout: ReturnType<typeof setTimeout>;
  ticket: {
    directory: string;
    url: string;
    embedurl: SafeResourceUrl;
    expiration: string;
    token: string;
  } = null;
  uploaderUrl = environment.uploaderurl;

  constructor(private apollo: Apollo,
              public dialogRef: DialogRef<GenerateUploadLinkComponent>,
              private clipboard: Clipboard,
              private banner: BannerService,
              private domSanitizer: DomSanitizer,
              @Inject(DIALOG_DATA) public data: { folder: Folder }) {
    this.folder = this.data.folder;
    const components = this.data.folder.id.split('/')
    components.pop()
    this.message = components.pop()
  }

  genLink(button: DoButtonComponent) {

    this.apollo.mutate<Mutation>({
      mutation: CREATE_UPLOAD_TICKET,
      variables: {
        folder_id: this.folder.id,
        message: this.message
      },
      fetchPolicy: 'network-only'
    }).subscribe({
      next: d => {
        button.done('Upload link generated.');
        const result = d.data?.createUploadTicket;
        if (!result) {
          button.done('No ticket returned.', {severity: 'cl-alert-danger'} );
          return;
        }
        this.ticket = {
          directory: encodeURIComponent(result.directory),
          url: result.url.toString(),
          embedurl: this.domSanitizer.bypassSecurityTrustResourceUrl(uploaderEmbedUrl(result.url)),
          expiration: result.expiration,
          token: result.token
        };
      },
      error: err => {
        console.error('Upload ticket creation failed', err);
        button.done('Failed to generate upload link.', {severity: 'cl-alert-danger'} );
      }
    });
  }

  get awsProfileText(): string {
    return `[profile ${this.message}]\n# Valid until ${this.ticket.expiration}\ncredential_process = curl ${this.uploaderUrl}/api_v1/upload/${this.ticket.directory}/${this.ticket.token}`;
  }

  copyClipboard() {
    this.copyToClipboard('link', this.ticket.url.toString());
  }

  copyAwsProfile() {
    this.copyToClipboard('aws', this.awsProfileText);
  }

  /**
   * Copies `text` and confirms it on the button that triggered it, so the user knows the click
   * did something. `Clipboard.copy` reports whether it worked — a failed copy is a real error and
   * must not be confirmed.
   */
  private copyToClipboard(source: 'link' | 'aws', text: string) {
    if (!this.clipboard.copy(text)) {
      this.banner.open('Could not copy to the clipboard, please select and copy the text manually.', ['CLOSE']);
      return;
    }
    this.copied.set(source);
    clearTimeout(this.copiedResetTimeout);
    this.copiedResetTimeout = setTimeout(() => this.copied.set(null), 2000);
  }

  downloadCyberduckBookmark() {
    // Generate Cyberduck bookmark with embedded credential endpoint
    const bookmark = this.generateCyberduckBookmark();

    // Create downloadable file
    const blob = new Blob([bookmark], { type: 'application/x-duck' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `${this.message || 'upload'}.duck`;
    link.click();
    window.URL.revokeObjectURL(url);
  }

  private generateCyberduckBookmark(): string {
    // Extract S3 endpoint from ticket URL for region
    const url = new URL(this.ticket.url);
    const region = url.hostname.match(/s3[.-]([^.]+)\.amazonaws\.com/)?.[1] || 'us-east-1';
    const nickname = this.message || 'upload';

    // ticket.directory contains "bucketname/path" - decode it first
    const decodedDirectory = decodeURIComponent(this.ticket.directory);

    // Split on first slash to separate bucket from path
    const firstSlashIndex = decodedDirectory.indexOf('/');
    const bucket = firstSlashIndex > -1 ? decodedDirectory.substring(0, firstSlashIndex) : decodedDirectory;
    const path = firstSlashIndex > -1 ? decodedDirectory.substring(firstSlashIndex + 1) : '';

    // Build credential endpoint URL with embedded token (use decoded directory path)
    const credentialUrl = `${this.uploaderUrl}/api_v1/upload/${decodedDirectory}${this.ticket.token}`;

    // Escape XML special characters
    const escapeXml = (str: string) => str
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&apos;');

    // Build hostname with bucket (virtual-hosted style, without region - matching working bookmark format)
    const hostname = `${bucket}.s3.amazonaws.com`;

    // Path needs leading slash to be interpreted as S3 prefix (matching working bookmark format)
    const bookmarkPath = path ? `/${escapeXml(path)}` : '/';

    // Generate a UUID for the bookmark
    const uuid = crypto.randomUUID();

    return `<?xml version="1.0" encoding="UTF-8"?>
<!DOCTYPE plist PUBLIC "-//Apple//DTD PLIST 1.0//EN" "http://www.apple.com/DTDs/PropertyList-1.0.dtd">
<plist version="1.0">
<dict>
    <key>Protocol</key>
    <string>s3</string>
    <key>Nickname</key>
    <string>${escapeXml(nickname)} - Valid until ${escapeXml(this.ticket.expiration)}</string>
    <key>UUID</key>
    <string>${uuid}</string>
    <key>Hostname</key>
    <string>${hostname}</string>
    <key>Port</key>
    <string>443</string>
    <key>Path</key>
    <string>${bookmarkPath}</string>
    <key>Custom</key>
    <dict>
        <key>s3.location</key>
        <string>${region}</string>
        <key>Context</key>
        <string>${escapeXml(credentialUrl)}</string>
    </dict>
</dict>
</plist>`;
  }

  ngOnInit() {
  }

  ngOnDestroy() {
    clearTimeout(this.copiedResetTimeout);
  }
}
